import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { IconCertificate } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';

import {
  AssignedStepStatusRequest,
  AssignmentLatestCompletionReport,
  ContentStep,
  getActivePlanAssignmentsQueryKey,
  getPlanAndAssignmentsOptions,
  markPlanAsComplete,
  Plan,
  updateStepStatus,
  type UserPlans,
} from '@cfa/pathway-fe-shared/xp';
import { Button, IconButton, LoadingIndicator } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import { useInvalidateCertificateQueries } from '@/hooks/useInvalidateCertificateQueries';

import { getCompletionStatusText } from '@/utils/getCompletionStatusText';
import { isValidPlanId } from '@/utils/isValidPlanId';
import { itemByLanguage } from '@/utils/language';
import {
  invalidateAllPlanQueries,
  invalidateCertificateQueries,
} from '@/utils/queryInvalidation';
import { isApiError } from '@/utils/request';
import {
  getDurationFromTotalSeconds,
  getHrsFromTotalMins,
  getMinsFromTotalMins,
  getTimeSpentOnPlanForTeamMember,
} from '@/utils/time';
import { withRoles } from '@/utils/withRoles';

import GenericError from '@/components/Error/GenericError';
import { Header } from '@/components/header';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import AssignmentStatusSummary from '@/components/PlanCards/AssignmentStatusSummary';
import PlanItemBrightcoveVideo from '@/components/PlanCards/PlanItemBrightcoveVideo';
import PlanItemCard from '@/components/PlanCards/PlanItemCard';
import PlanItemProcedure from '@/components/PlanCards/PlanItemProcedure';
import PlanItemQuiz from '@/components/PlanCards/PlanItemQuiz';
import PlanItemTask from '@/components/PlanCards/PlanItemTask';
import ConfirmationModal from '@/components/popups/ConfirmationModal';
import {
  PrintCertificateModals,
  usePrintCertificates,
} from '@/components/PrintCertificates';

import Constants, { routes } from '@/constants';

import { queryClient } from '@/services';

import PlanCompleteModal from './PlanCompleteModal';

import { userPrefsAtom } from '@/atoms/userPrefs';
import PageNotFound404 from '@/routes/404/404';
import { getPlanStepResourceReference } from '@/routes/TrainingPlans/getPlanStepResourceReference';
import { useIsAssignedToPlan } from '@/routes/TrainingPlans/hooks/useIsAssignedToPlan';
import { useTrackPlanResourceErrors } from '@/routes/TrainingPlans/hooks/useTrackPlanResourceErrors';
import useTrainingHeartbeatPlanTimer from '@/routes/TrainingPlans/hooks/useTrainingHeartbeatTimer';
import { isCountableStep } from '@/routes/TrainingPlans/isCountableStep';

type AssignedPlanWithContent = UserPlans & {
  plan: Plan & { content: NonNullable<Plan['content']> };
};

const hasAssignedPlanContent = (
  data: UserPlans | undefined,
): data is AssignedPlanWithContent => !!data?.plan?.content;

const AssignedPlanView = () => {
  const invalidateCertificates = useInvalidateCertificateQueries();
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { certificatesEpic, planDescriptions } = useFlags();
  const { data: auth } = useAuth();
  const userId = auth?.userId ?? '';
  const safePlanId = planId ?? '';

  const { language: userLanguage } = useAtomValue(userPrefsAtom);

  const [activeTask, setActiveTask] = useState('');
  const [activeQuizStepId, setActiveQuizStepId] = useState('');
  const [apiError, setApiError] = useState<Error | null>(null);
  const [showPlanCompleteModal, setShowPlanCompleteModal] = useState(false);
  const triggeredFromCompletionRef = useRef(false);

  const isPlanIdValid = !!planId && isValidPlanId(planId);

  const { isAssigned, isAssignmentCheckError, assignmentCheckError } =
    useIsAssignedToPlan(safePlanId, {
      enabled: isPlanIdValid,
    });

  const {
    data: plan,
    error: planError,
    refetch: _refetchPlan,
    isPending: isPendingPlan,
    isFetching: isFetchingPlan,
  } = useQuery({
    ...getPlanAndAssignmentsOptions({ path: { planId: safePlanId } }),
    enabled: isPlanIdValid && isAssigned === true,
  });

  const refetchPlan = () => {
    if (!isValidPlanId(planId)) {
      throw new Error('Invalid planId');
    }
    return _refetchPlan();
  };

  const {
    mutateAsync: updatePlanStepStatus,
    isPending: updatePlanStepStatusIsLoading,
  } = useMutation({
    mutationFn: ({
      planId,
      stepId,
      stepStatus,
    }: {
      planId: string;
      stepId: string;
      stepStatus: AssignedStepStatusRequest['status'];
    }) =>
      updateStepStatus({
        path: { planId, stepId },
        body: {
          status: stepStatus,
        },
      }),
  });
  const header = itemByLanguage(plan?.plan?.name);
  const planDescription = itemByLanguage(plan?.plan?.description);

  const {
    mutateAsync: updateUserPlanToComplete,
    isPending: updateUserPlanToCompleteIsLoading,
  } = useMutation({
    mutationFn: ({ planId }: { planId: string }) =>
      markPlanAsComplete({
        path: { planId },
      }),
  });

  const userPlanStatus = plan?.assignments?.find(
    assignment => assignment.userId === userId,
  );

  const timeSpentOnPlan = getTimeSpentOnPlanForTeamMember(userPlanStatus);
  const timeSpentHrs = getHrsFromTotalMins(timeSpentOnPlan);
  const timeSpentMins = getMinsFromTotalMins(timeSpentOnPlan);

  const previousAssignmentData = userPlanStatus?.latestCompletionReport;
  const previousAssignmentCompletionDate =
    previousAssignmentData?.completionDate ?? userPlanStatus?.completionDate;
  const {
    hours: previousAssignmentTimeSpentHrs,
    minutes: previousAssignmentTimeSpentMins,
  } = getDurationFromTotalSeconds(
    previousAssignmentData?.totalDurationInSeconds ?? 0,
  );

  const completedStatusText = userPlanStatus
    ? getCompletionStatusText({
        timeSpentHrs,
        timeSpentMins,
        completionDate:
          userPlanStatus.latestCompletionReport?.completionDate ??
          userPlanStatus.completionDate,
        expirationDate: (
          userPlanStatus.latestCompletionReport as
            | (AssignmentLatestCompletionReport & {
                expirationDate?: string;
              })
            | undefined
        )?.expirationDate,
        // These two props can be removed once the certificates epic is fully rolled out
        certificatesEpicEnabled: certificatesEpic,
        planType: plan?.plan?.type,
      })
    : '';

  const isPlanComplete =
    userPlanStatus?.status === Constants.TRAINING_PLANS.COMPLETED;

  const certificateHook = usePrintCertificates({
    planId,
    teamMemberId: userId,
  });

  const locationNumber =
    auth?.locationsWithRoles.at(0)?.location.number ?? 'Unknown';

  useTrackPlanResourceErrors({
    planData: plan,
    planId,
    language: userLanguage,
    locationNumber,
    planType: plan?.plan?.type,
    enabled: !isPendingPlan && !!plan,
  });

  const users = useMemo(() => [userId], [userId]);

  const handleSetActiveQuiz = useCallback((step: ContentStep) => {
    const stepId = step.id;
    setActiveQuizStepId(prevId => (prevId === stepId ? '' : stepId));
  }, []);

  const handleSetActiveTask = useCallback((step: ContentStep) => {
    const stepId = step.id;
    setActiveTask(prevId => (prevId === stepId ? '' : stepId));
  }, []);

  const handleSetActiveVideo = useCallback((step: ContentStep) => {
    const stepId = step.id;
    setActiveTask(prevId => (prevId === stepId ? '' : stepId));
  }, []);

  const isStepCompleted = (stepId?: string) => {
    if (!stepId) {
      return false;
    }
    return (
      userPlanStatus?.steps?.find(step => step.stepId === stepId)?.status ===
      Constants.TRAINING_PLANS.COMPLETE
    );
  };

  const getStepStatus = (status: boolean) =>
    status ? 'COMPLETE' : 'INCOMPLETE';

  const [startTaskTimer, stopTaskTimer] = useTrainingHeartbeatPlanTimer(
    safePlanId,
    activeTask,
    [userId],
    getStepStatus(isStepCompleted(activeTask)),
  );

  useEffect(() => {
    if (activeQuizStepId !== '' && isStepCompleted(activeQuizStepId)) {
      setActiveQuizStepId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuizStepId, plan]);

  // timer for tasks
  useEffect(() => {
    if (activeTask !== '') {
      startTaskTimer();
    } else {
      stopTaskTimer();
    }
    return () => {
      stopTaskTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTask]);

  //Error Handling
  if (!planId || !isPlanIdValid) {
    return <PageNotFound404 />;
  }

  if (isAssignmentCheckError) {
    datadogRum.addError(
      assignmentCheckError instanceof Error
        ? assignmentCheckError
        : new Error(JSON.stringify(assignmentCheckError)),
    );
    return <GenericError />;
  }

  if (isAssigned === false) {
    return <PageNotFound404 />;
  }

  // Ignore cached detail errors while the assignment check is still pending
  // (detail query is disabled until isAssigned === true).
  if (isAssigned === true && isApiError(planError)) {
    if (planError.status === 404) {
      datadogRum.addError(
        planError instanceof Error
          ? planError
          : new Error(JSON.stringify(planError)),
      );
      return <PageNotFound404 />;
    } else {
      datadogRum.addError(
        planError instanceof Error
          ? planError
          : new Error(JSON.stringify(planError)),
      );
      return <GenericError />;
    }
  }

  const isPlanContentLoading =
    isAssigned === undefined ||
    isPendingPlan ||
    (isFetchingPlan && !hasAssignedPlanContent(plan));

  if (!isPlanContentLoading && !hasAssignedPlanContent(plan)) {
    return <PageNotFound404 />;
  }

  const navigateAfterSuccess = (planName?: Record<string, string>) => {
    navigate(
      {
        pathname: routes.plans.plans,
      },
      {
        state: { completedPlan: itemByLanguage(planName) },
      },
    );
  };

  const onCardClick = (step: ContentStep) => {
    if (!getPlanStepResourceReference(step)) {
      datadogRum.addError(
        new Error(`Unable to resolve reference for step id: ${step.id}`),
      );
      return;
    }

    navigate({
      pathname: routes.plans.planDocument({
        planId,
        stepId: step.id,
      }),
    });
  };

  const handlePlanItemCompleteByStepId = (stepId?: string) => {
    if (!stepId) {
      return;
    }
    handlePlanItemComplete(stepId);
  };

  const handlePlanItemComplete = (id: string) => {
    const isCompleted = !isStepCompleted(id);
    const stepStatus = getStepStatus(isCompleted);
    if (!planId) {
      return;
    }
    if (!isPlanComplete) {
      if (id === activeTask) {
        setActiveTask('');
      }
      updatePlanStepStatus({
        planId,
        stepId: id,
        stepStatus,
      })
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: getActivePlanAssignmentsQueryKey(),
            refetchType: 'all',
          });
          refetchPlan();
        })
        .catch((err: unknown) => {
          datadogRum.addError(
            err instanceof Error ? err : new Error(String(err)),
          );
        });
    }
  };

  const handleGetCertificate = () => {
    setShowPlanCompleteModal(false);
    triggeredFromCompletionRef.current = true;
    certificateHook.handleClick();
  };

  const handleCloseCertificateViewModalWithOptionalNavigate = () => {
    certificateHook.handleCloseViewModal();
    if (triggeredFromCompletionRef.current) {
      triggeredFromCompletionRef.current = false;
      navigateAfterSuccess(plan?.plan?.name);
    }
  };

  const onCompleteButtonClick = (planName?: Record<string, string>) => {
    if (!planId) {
      return;
    }

    updateUserPlanToComplete({ planId })
      .then(async () => {
        await Promise.all([
          invalidateAllPlanQueries(planId),
          invalidateCertificates({ planId, userId }),
        ]);
        invalidateCertificateQueries(planId, [userId]);

        if (certificatesEpic) {
          setShowPlanCompleteModal(true);
        } else {
          navigateAfterSuccess(planName);
        }
      })
      .catch((err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        setApiError(error);
        datadogRum.addError(error);
      });
  };

  const renderCompleteButton = (planName?: Record<string, string>) => (
    <div className="mt-2.5 flex justify-center">
      <Button
        color={'secondary'}
        data-testid="CompleteButton"
        isDisabled={
          !areAllStepsComplete() ||
          isPlanComplete ||
          updateUserPlanToCompleteIsLoading
        }
        onPressEnd={() => onCompleteButtonClick(planName)}
        variant={isPlanComplete ? 'text' : 'filled'}
      >
        {updatePlanStepStatusIsLoading && <LoadingIndicator size="sm" />}
        {updatePlanStepStatusIsLoading
          ? t('Generic.loading')
          : isPlanComplete
            ? t('Button.completed')
            : t('Button.complete')}
      </Button>
    </div>
  );

  // Due to legacy decisions, the only reliable use of the 'available' property on a resource is with type === "document".
  // Therefore, we include all resources in sections and checklists, but exclude documents where 'available' === false.
  const areAllStepsComplete = () => {
    const stepsFromChecklist = plan?.plan?.content?.sections
      ?.flatMap(section => section.steps?.filter(isCountableStep))
      .concat(plan.plan.content.steps?.filter(isCountableStep));

    const stepsFromStatus = userPlanStatus?.steps;

    const matchedSteps = stepsFromStatus?.filter(step =>
      stepsFromChecklist?.map(it => it?.id).includes(step.stepId),
    );

    return (
      matchedSteps?.every(
        step => step.status === Constants.TRAINING_PLANS.COMPLETE,
      ) && matchedSteps.length === stepsFromChecklist?.length
    );
  };

  return (
    <>
      <Header.Root>
        <Header.Back
          to={routes.plans.plans}
          tx="TrainingPlans.filtering.myPlans"
        />
        <Header.Title>{header}</Header.Title>
        {certificateHook.shouldShow && (
          <Header.ActionIconButton
            data-testid="certificate-icon-button"
            icon={IconCertificate}
            onClick={certificateHook.handleClick}
            tooltip="Button.getCertificates"
            disabled={certificateHook.isLoading}
          />
        )}
      </Header.Root>
      <LoadingOverlay isOpen={isPlanContentLoading} fixed />
      {!isPlanContentLoading && (
        <div className="centered-content md:mt-header-height-with-content-spacing px-4 max-md:mt-6">
          <div className="flex items-center justify-between">
            <p className="text-variant-h3 text-left">{header}</p>
            {certificateHook.shouldShow && (
              <IconButton
                className="p-0! md:hidden"
                color="secondary"
                onPress={certificateHook.handleClick}
                isDisabled={certificateHook.isLoading}
                data-testid="certificate-button-mobile"
              >
                <IconCertificate width={24} height={24} />
              </IconButton>
            )}
          </div>
          {planDescriptions && planDescription && (
            <p
              className="text-variant-body1 mt-2 font-medium"
              data-testid="AssignedPlanDescription"
            >
              {planDescription}
            </p>
          )}
          <AssignmentStatusSummary
            completedStatusText={completedStatusText}
            isCurrentAssignmentCompleted={isPlanComplete}
            previousAssignmentCompletionDate={previousAssignmentCompletionDate}
            previousAssignmentTimeSpentHrs={previousAssignmentTimeSpentHrs}
            previousAssignmentTimeSpentMins={previousAssignmentTimeSpentMins}
            timeSpentHrs={timeSpentHrs}
            timeSpentMins={timeSpentMins}
          />
          {plan?.plan?.content?.steps?.map((step, index) => {
            switch (step.type) {
              case Constants.STEP_TYPES.QUIZ:
                return (
                  <PlanItemCard key={index}>
                    <PlanItemQuiz
                      disabled={isPlanComplete}
                      isActive={activeQuizStepId === step.id}
                      isComplete={isStepCompleted(step.id)}
                      language={userLanguage}
                      onClick={handleSetActiveQuiz}
                      onRefetch={refetchPlan}
                      planId={plan.plan?.id ?? ''}
                      statuses={plan.assignments ?? []}
                      step={step}
                      users={users}
                    />
                  </PlanItemCard>
                );
              case Constants.STEP_TYPES.TASK:
                return (
                  <PlanItemCard key={index}>
                    <PlanItemTask
                      disabled={isPlanComplete}
                      isActive={activeTask === step.id}
                      isComplete={isStepCompleted(step.id)}
                      onClick={handleSetActiveTask}
                      onCompleteToggle={() =>
                        handlePlanItemCompleteByStepId(step.id)
                      }
                      planId={plan.plan?.id ?? ''}
                      statuses={plan.assignments ?? []}
                      step={step}
                      userId={userId}
                    />
                  </PlanItemCard>
                );
              case Constants.STEP_TYPES.DOCUMENT: {
                const stepResourceType = step.resource?.type?.toLowerCase();
                return stepResourceType ===
                  Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE ? (
                  <PlanItemCard key={index}>
                    <PlanItemBrightcoveVideo
                      disabled={isPlanComplete}
                      isActive={activeTask === step.id}
                      isComplete={isStepCompleted(step.id)}
                      onClick={handleSetActiveVideo}
                      onCompleteToggle={() =>
                        handlePlanItemCompleteByStepId(step.id)
                      }
                      statuses={plan.assignments ?? []}
                      step={step}
                      users={users}
                    />
                  </PlanItemCard>
                ) : (
                  <PlanItemCard key={index}>
                    <PlanItemProcedure
                      disabled={isPlanComplete}
                      isActive={activeTask === step.id}
                      isComplete={isStepCompleted(step.id)}
                      isMissing={!step.available}
                      onClick={() => onCardClick(step)}
                      onCompleteToggle={() =>
                        handlePlanItemCompleteByStepId(step.id)
                      }
                      statuses={plan.assignments ?? []}
                      step={step}
                      userId={userId}
                      language={userLanguage}
                    />
                  </PlanItemCard>
                );
              }
              default:
                return null;
            }
          })}
          {plan?.plan?.content?.sections?.map(
            section =>
              !!section.steps?.length && (
                <PlanItemCard
                  key={section.id}
                  sectionName={itemByLanguage(section.name)}
                >
                  {section.steps.map((step, stepIndex) => {
                    switch (step.type) {
                      case Constants.STEP_TYPES.QUIZ:
                        return (
                          <PlanItemQuiz
                            disabled={isPlanComplete}
                            isActive={activeQuizStepId === step.id}
                            isComplete={isStepCompleted(step.id)}
                            key={stepIndex}
                            language={userLanguage}
                            onClick={handleSetActiveQuiz}
                            onRefetch={refetchPlan}
                            planId={plan.plan?.id ?? ''}
                            statuses={plan.assignments ?? []}
                            step={step}
                            users={users}
                          />
                        );
                      case Constants.STEP_TYPES.TASK:
                        return (
                          <PlanItemTask
                            disabled={isPlanComplete}
                            isActive={activeTask === step.id}
                            isComplete={isStepCompleted(step.id)}
                            key={stepIndex}
                            onClick={handleSetActiveTask}
                            onCompleteToggle={() =>
                              handlePlanItemCompleteByStepId(step.id)
                            }
                            planId={plan.plan?.id ?? ''}
                            statuses={plan.assignments ?? []}
                            step={step}
                            userId={userId}
                          />
                        );
                      case Constants.STEP_TYPES.DOCUMENT: {
                        const stepResourceType =
                          step.resource?.type?.toLowerCase();
                        return stepResourceType ===
                          Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE ? (
                          <PlanItemBrightcoveVideo
                            disabled={isPlanComplete}
                            isActive={activeTask === step.id}
                            isComplete={isStepCompleted(step.id)}
                            key={stepIndex}
                            onClick={handleSetActiveVideo}
                            onCompleteToggle={() =>
                              handlePlanItemCompleteByStepId(step.id)
                            }
                            statuses={plan.assignments ?? []}
                            step={step}
                            users={users}
                          />
                        ) : (
                          <PlanItemProcedure
                            disabled={isPlanComplete}
                            isActive={activeTask === step.id}
                            isComplete={isStepCompleted(step.id)}
                            isMissing={!step.available}
                            key={stepIndex}
                            onClick={() => onCardClick(step)}
                            onCompleteToggle={() =>
                              handlePlanItemCompleteByStepId(step.id)
                            }
                            statuses={plan.assignments ?? []}
                            step={step}
                            userId={userId}
                            language={userLanguage}
                          />
                        );
                      }
                      default:
                        return null;
                    }
                  })}
                </PlanItemCard>
              ),
          )}
          {plan?.plan?.name && renderCompleteButton(plan.plan.name)}
          <ConfirmationModal
            bodyText={t('GenericError.problemCompletingAction')}
            headerText={t('GenericError.error')}
            isOpen={!!apiError}
            onClose={() => setApiError(null)}
            primaryButtonColor="primary"
            primaryButtonHandler={() => setApiError(null)}
            primaryButtonText={t('Button.close')}
            className="mt-"
          />
          {certificatesEpic && plan?.plan?.name && (
            <PlanCompleteModal
              isOpen={showPlanCompleteModal}
              onClose={() => {
                setShowPlanCompleteModal(false);
                navigateAfterSuccess();
              }}
              onGetCertificate={handleGetCertificate}
            />
          )}

          <PrintCertificateModals
            {...certificateHook}
            handleCloseViewModal={
              handleCloseCertificateViewModalWithOptionalNavigate
            }
          />
        </div>
      )}
    </>
  );
};

const AssignedPlanViewWithRoles = withRoles(AssignedPlanView, 'plans');

export default AssignedPlanViewWithRoles;
